import { defineStore } from "pinia";

import posthog from "posthog-js";

import { sdk } from "@/js/sdk";
import type { SubscriptionPlan } from "@signaloid/cloud-compute-engine-client";
import { useUserStore } from "./user";
import { OrganizationInvitation } from "@/types/api/organizations";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { ObjectValues } from "@/types/utilities";

// State of the background account-initialization work (org-invitation check,
// tier subscription, session refresh). It runs while the user answers the
// onboarding questions, so they do not wait on a blocking screen.
export const AccountInitStateE = {
	Idle: "Idle",
	Initializing: "Initializing",
	Ready: "Ready",
	Error: "Error",
} as const;
export type AccountInitState = ObjectValues<typeof AccountInitStateE>;

type State = {
	accountInitState: AccountInitState;
	initStarted: boolean;
	initStartedAt: number | null;
	proTrialFailed: boolean;
	invitation: OrganizationInvitation | null;
	subscribedTier: SubscriptionPlan | null;
	role: string | null;
	industry: string | null;
};

const TRIAL_TIER: SubscriptionPlan = "ProTier";

export const useOnboardingStore = defineStore("onboarding", {
	state: (): State => ({
		accountInitState: AccountInitStateE.Idle,
		initStarted: false,
		initStartedAt: null,
		proTrialFailed: false,
		invitation: null,
		subscribedTier: null,
		role: null,
		industry: null,
	}),
	getters: {
		accountReady(state): boolean {
			return state.accountInitState === AccountInitStateE.Ready;
		},
		accountInitFailed(state): boolean {
			return state.accountInitState === AccountInitStateE.Error;
		},
	},
	actions: {
		// Idempotent: only the first call runs the work.
		async startAccountInitialization() {
			if (this.initStarted) {
				return;
			}
			this.initStarted = true;
			this.initStartedAt = Date.now();
			await this.runAccountInitialization();
		},
		async retryAccountInitialization() {
			await this.runAccountInitialization();
		},
		initDurationMs(): number | undefined {
			return this.initStartedAt !== null ? Date.now() - this.initStartedAt : undefined;
		},
		async runAccountInitialization() {
			this.accountInitState = AccountInitStateE.Initializing;

			let invitation: OrganizationInvitation | null = null;
			try {
				const invitationsResponse = await sdk.organizations.listInvitations();
				if (invitationsResponse.Count > 0 && invitationsResponse.Invitations.length > 0) {
					invitation = invitationsResponse.Invitations[0];
				}
			} catch {
				// No invitation, or the lookup failed: proceed with the default trial.
			}
			this.invitation = invitation;

			if (invitation) {
				await this.subscribeToEnterpriseTier(invitation);
			} else {
				await this.subscribeToProTrial();
			}
		},
		async subscribeToProTrial() {
			const userStore = useUserStore();
			try {
				await userStore.getCurrentUserSubscriptions();
				await sdk.subscriptions.update({ Tier: TRIAL_TIER }, { freeTrialOption: true });
				await userStore.refreshCurrentUserSession();
				this.subscribedTier = TRIAL_TIER;
				this.accountInitState = AccountInitStateE.Ready;
				posthog.capture("onboarding_tier_selected", {
					tier: TRIAL_TIER,
					had_org_invitation: false,
					fell_back_to_free_tier: false,
					duration_ms: this.initDurationMs(),
				});
			} catch (error) {
				monitoringCaptureError(error, "Onboarding: Pro trial subscription failed; falling back to Free tier");
				this.proTrialFailed = true;
				await this.subscribeToFreeTier();
			}
		},
		async subscribeToFreeTier() {
			const userStore = useUserStore();
			try {
				await sdk.subscriptions.update({ Tier: "FreeTier" });
				await userStore.refreshCurrentUserSession();
				this.subscribedTier = "FreeTier";
				this.accountInitState = AccountInitStateE.Ready;
				posthog.capture("onboarding_tier_selected", {
					tier: "FreeTier",
					had_org_invitation: !!this.invitation,
					fell_back_to_free_tier: this.proTrialFailed,
					duration_ms: this.initDurationMs(),
				});
			} catch (error) {
				monitoringCaptureError(error, "Onboarding: subscribe to free tier");
				this.accountInitState = AccountInitStateE.Error;
				posthog.capture("onboarding_initialization_failed", { stage: "free_tier" });
			}
		},
		async subscribeToEnterpriseTier(invitation: OrganizationInvitation) {
			const userStore = useUserStore();
			try {
				const expectedOrgId = invitation.OrganizationID;
				if (!expectedOrgId) {
					this.accountInitState = AccountInitStateE.Error;
					posthog.capture("onboarding_initialization_failed", { stage: "missing_organization_id" });
					return;
				}

				// invitation.Tier is an unvalidated backend string. An unknown tier
				// code makes the update fail, which is caught below.
				await sdk.subscriptions.update({ Tier: invitation.Tier as SubscriptionPlan });

				// Retry until organization_id appears in the refreshed token, or attempts run out.
				let organizationId: string | null = null;
				let attempts = 0;
				const maxAttempts = 5;
				while (organizationId !== expectedOrgId && attempts < maxAttempts) {
					await new Promise((resolve) => setTimeout(resolve, 1000));
					await userStore.refreshCurrentUserSession();
					organizationId = await userStore.getCurrentUserOrganizationID();
					attempts++;
				}

				// org_id never propagated: fail rather than report a ready account on a bad token.
				if (organizationId !== expectedOrgId) {
					this.accountInitState = AccountInitStateE.Error;
					monitoringCaptureError(
						new Error(`Organization ID mismatch after ${attempts} attempts`),
						"Onboarding: enterprise org_id did not propagate",
					);
					posthog.capture("onboarding_initialization_failed", {
						stage: "enterprise_org_id_timeout",
						attempts,
					});
					return;
				}

				this.subscribedTier = invitation.Tier as SubscriptionPlan;
				this.accountInitState = AccountInitStateE.Ready;
				posthog.capture("onboarding_tier_selected", {
					tier: invitation.Tier,
					had_org_invitation: true,
					fell_back_to_free_tier: false,
					organization_id: invitation.OrganizationID,
					duration_ms: this.initDurationMs(),
				});
			} catch (error) {
				monitoringCaptureError(error, "Onboarding: subscribe to enterprise tier");
				this.accountInitState = AccountInitStateE.Error;
				posthog.capture("onboarding_initialization_failed", { stage: "enterprise" });
			}
		},
		setRole(role: string) {
			this.role = role;
		},
		setIndustry(industry: string) {
			this.industry = industry;
		},
		// Never throws: persistence failure must not block onboarding completion.
		async submitProfileAnswers() {
			try {
				const userStore = useUserStore();
				// user.* namespace matches App.vue; keeps role/industry queryable as cohorts.
				posthog.setPersonProperties({
					"user.role": this.role ?? undefined,
					"user.industry": this.industry ?? undefined,
				});
				posthog.capture("onboarding_profile_submitted", {
					role: this.role,
					industry: this.industry,
					role_skipped: this.role === null,
					industry_skipped: this.industry === null,
				});
				await userStore.syncUserProfileWithRemote({ Role: this.role, Industry: this.industry });
			} catch (error) {
				monitoringCaptureError(error, "Onboarding: submit profile answers");
			}
		},
		reset() {
			this.accountInitState = AccountInitStateE.Idle;
			this.initStarted = false;
			this.initStartedAt = null;
			this.proTrialFailed = false;
			this.invitation = null;
			this.subscribedTier = null;
			this.role = null;
			this.industry = null;
		},
	},
});
